import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@/shared/ui/icon';
import { useAuth } from '@/features/identity-management/auth/hooks/useAuth';
import { useAccountAvatar } from '@/features/identity-management/account/hooks/useAccountAvatar';
import { useCandidateImages } from '@/features/candidate-management/candidates/hooks/useCandidateImages';
import { useCandidateProfile } from '@/features/candidate-management/candidates/hooks/useCandidateProfile';
import { ROUTES } from '@/app/router/route-paths';
import { useT } from '@/shared/i18n';

function userDisplayName(user: ReturnType<typeof useAuth>['user']): string {
  if (!user) return '';
  return [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
}

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

/**
 * Authenticated user control.
 *
 * Candidates see the SAME avatar + name as their Smart Profile (the candidate
 * image + profile display name), so identity is consistent across the app. Other
 * actors fall back to the universal account avatar + auth name. The actor-split
 * keeps candidate-only endpoints from firing for employers/admins.
 *
 * `compact` (the desktop icon rail) renders only a circular avatar with a Lime
 * ring; the menu opens to the side. The default renders the avatar plus name /
 * email and opens below — used inside the mobile drawer footer.
 */
export function AppUserMenu({
  compact = false,
}: {
  compact?: boolean;
}): ReactNode {
  const { user } = useAuth();
  const isCandidate = (user?.primaryActorType ?? 'candidate') === 'candidate';
  return isCandidate ? (
    <CandidateUserMenu compact={compact} />
  ) : (
    <AccountUserMenu compact={compact} />
  );
}

function CandidateUserMenu({ compact }: { compact: boolean }): ReactNode {
  const { user } = useAuth();
  const { avatarUrl } = useCandidateImages();
  const { profile } = useCandidateProfile();
  const candidateName = profile?.displayName?.trim() || '';
  return (
    <AppUserMenuView
      compact={compact}
      avatarUrl={avatarUrl ?? user?.avatarUrl ?? null}
      nameOverride={candidateName}
    />
  );
}

function AccountUserMenu({ compact }: { compact: boolean }): ReactNode {
  const { user } = useAuth();
  const { avatarUrl } = useAccountAvatar();
  return (
    <AppUserMenuView
      compact={compact}
      avatarUrl={avatarUrl ?? user?.avatarUrl ?? null}
      nameOverride=""
    />
  );
}

function AppUserMenuView({
  compact,
  avatarUrl,
  nameOverride,
}: {
  compact: boolean;
  avatarUrl: string | null;
  nameOverride: string;
}): ReactNode {
  const { t } = useT();
  const { user, logout, isLoading } = useAuth();
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const name = nameOverride || userDisplayName(user);
  const displayName = isLoading
    ? t('appShell.userMenu.loading')
    : name || t('appShell.userMenu.nameUnavailable');
  const displayEmail = user?.email || t('appShell.userMenu.emailUnavailable');

  useEffect(() => {
    if (!open) return;
    menuRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function onKeyDown(event: globalThis.KeyboardEvent): void {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  // Close when clicking anywhere outside the button + menu.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: globalThis.MouseEvent): void {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  function onButtonKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen(true);
    }
  }

  async function onLogout(): Promise<void> {
    setOpen(false);
    await logout();
  }

  const avatar = (
    <span
      className={
        compact
          ? 'flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border-2 border-secondary-fixed/40 bg-[var(--ink-band-raised)] text-sm font-semibold text-[var(--on-ink-band)] transition-colors group-hover:border-secondary-fixed'
          : 'flex h-9 w-9 items-center justify-center overflow-hidden rounded-md bg-[var(--surface-overlay)] text-sm font-semibold text-[var(--on-ink-band)]'
      }
    >
      {avatarUrl ? (
        <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        initials(name) || <Icon name="user" size={18} />
      )}
    </span>
  );

  return (
    <div className={compact ? 'relative' : 'relative min-w-0 flex-1'}>
      <button
        ref={buttonRef}
        type="button"
        aria-label={t('appShell.userMenu.open')}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={onButtonKeyDown}
        className={
          compact
            ? 'group inline-flex items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed/60'
            : 'inline-flex w-full min-w-0 items-center gap-3 rounded-md border border-[var(--ink-band-border)] bg-[var(--ink-band-raised)] px-2 py-1.5 text-start transition-colors hover:bg-[var(--surface-overlay)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed/60'
        }
      >
        {avatar}
        {compact ? null : (
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-[var(--on-ink-band)]">
              {displayName}
            </span>
            <span className="block truncate text-xs text-[var(--on-ink-band-variant)]">
              {displayEmail}
            </span>
          </span>
        )}
      </button>

      {open ? (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          tabIndex={-1}
          className={
            compact
              ? 'absolute bottom-0 start-[calc(100%+0.75rem)] z-50 w-64 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-raised)] p-2 shadow-panel focus-visible:outline-none'
              : 'absolute inset-x-0 bottom-[calc(100%+0.5rem)] z-50 w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-raised)] p-2 shadow-panel focus-visible:outline-none'
          }
        >
          <div className="border-b border-[var(--border-subtle)] px-3 py-3">
            <p className="truncate text-sm font-semibold text-[var(--text-primary)]">
              {displayName}
            </p>
            <p className="truncate text-xs text-[var(--text-secondary)]">{displayEmail}</p>
          </div>
          <Link
            to={ROUTES.app.account}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="mt-2 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-[var(--text-primary)] transition-colors hover:bg-[var(--surface-overlay)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed/60"
          >
            <Icon name="settings" size={16} />
            {t('appShell.userMenu.account')}
          </Link>
          <button
            type="button"
            role="menuitem"
            disabled={isLoading}
            onClick={() => void onLogout()}
            className="mt-1 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-[var(--text-primary)] transition-colors hover:bg-[var(--surface-overlay)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed/60 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Icon name="logout" size={16} />
            {t('auth.logout')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
